import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Crown, Sparkles, Gift } from 'lucide-react';
import { toast } from 'sonner';

export default function VIPCodeDialog({ open, onOpenChange, userId, onSuccess, onSkip }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!code.trim()) {
      toast.error('Please enter a VIP code');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-vip-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, code: code.toUpperCase() })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('🎉 ' + data.message);
        onSuccess();
      } else {
        toast.error('❌ ' + data.message);
      }

    } catch (error) {
      console.error('VIP code error:', error);
      toast.error('Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onSkip();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Gift className="w-8 h-8 text-white" />
            </div>
          </div>
          <DialogTitle className="text-2xl text-center">
            Unlock VIP Access! 🎁
          </DialogTitle>
          <DialogDescription className="text-center">
            Enter your VIP access code to unlock unlimited AI-optimized prompts and exclusive Pro features!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 my-4">
          <div className="space-y-2">
            <Label htmlFor="vip-code">VIP Access Code</Label>
            <Input
              id="vip-code"
              type="password"
              placeholder="Enter your code"
              className="text-center text-lg tracking-wider font-mono uppercase"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={10}
              disabled={loading}
            />
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Crown className="w-4 h-4 text-primary" />
              <span className="font-medium">Pro Benefits:</span>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 ml-6">
              <li>✨ Unlimited prompt generation</li>
              <li>⚡ 2x faster AI processing</li>
              <li>🎯 Advanced prompting techniques</li>
              <li>💎 Priority support</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button 
            onClick={handleVerify} 
            className="w-full bg-gradient-to-r from-primary to-secondary"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <Crown className="mr-2 h-4 w-4" />
                Verify Code
              </>
            )}
          </Button>
          <Button 
            onClick={handleSkip} 
            variant="outline" 
            className="w-full"
            disabled={loading}
          >
            Continue as Free User (10 prompts)
          </Button>
        </DialogFooter>

        <p className="text-xs text-center text-muted-foreground mt-4">
          Don&apos;t have a code? Continue as a free user and upgrade later from the pricing page.
        </p>
      </DialogContent>
    </Dialog>
  );
}
